import {
  Button,
  Icons,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { SlackWorkspaceData } from '@cast/types';

import { SlackChannelSelect } from './SlackChannelSelect';
import { SlackIntegrationStatus } from './types';

type SlackIntegrationBlockProps = {
  status?: SlackIntegrationStatus;
  onConnect?: () => void;
  onRetry?: () => void;
  onDisconnect?: () => void;
  workspaceData?: SlackWorkspaceData;
};

export const SlackIntegrationBlock = ({
  status = 'not_connected',
  onConnect,
  onRetry,
  onDisconnect,
  workspaceData,
}: SlackIntegrationBlockProps) => {
  return (
    <Stack mt={16} gap="8px">
      <Typography variant="P12M" color={tokens.theme.fg.structural.primary}>
        Workspace
      </Typography>
      {status === 'not_connected' && (
        <Box
          sx={{
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutralStrong,
            borderRadius: '4px',
            padding: '17px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Connect your Slack workspace to receive alerts in selected channels.
          </Typography>
          <Button
            variant="ghost"
            size="sm"
            onClick={onConnect}
            testId="connect-slack-button"
            endIcon={<Icons.Globe size={16} />}
          >
            Connect
          </Button>
        </Box>
      )}
      {status === 'pending' && (
        <Box
          sx={{
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutralStrong,
            borderRadius: '4px',
            padding: '17px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '32px',
          }}
        >
          <Stack gap="0px" flex="1">
            <Stack direction="row" alignItems="center" gap="8px" mb="4px">
              <Box
                sx={{
                  backgroundColor: tokens.theme.bg.structural.surfaceStrong,
                  borderRadius: '4px',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icons.Clock
                  size={16}
                  color={tokens.theme.fg.structural.secondary}
                />
              </Box>
              <Typography
                variant="A1"
                color={tokens.theme.fg.structural.primary}
              >
                Pending
              </Typography>
            </Stack>
            <Box pl="32px">
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                Your Slack workspace admin must approve this connection.
                <br />
                The request expires in 7 days.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="ghost"
            size="sm"
            onClick={onRetry}
            testId="retry-slack-button"
            endIcon={<Icons.ArrowClockwise size={16} />}
          >
            Retry
          </Button>
        </Box>
      )}
      {status === 'connected' && (
        <Box
          sx={{
            backgroundColor: tokens.theme.bg.structural.surfaceSubtle,
            border: '1px solid',
            borderColor: tokens.theme.border.structural.neutralStrong,
            borderRadius: '4px',
            padding: '16px',
          }}
        >
          <Stack gap="16px">
            {/* Header with logo and disconnect button */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" alignItems="center" gap="8px">
                <Box
                  sx={{
                    backgroundColor: tokens.theme.bg.action.primary,
                    borderRadius: '4px',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={workspaceData?.iconUrl}
                    style={{ width: '100%', height: '100%' }}
                  />
                </Box>
                <Typography
                  variant="A1"
                  color={tokens.theme.fg.structural.primary}
                >
                  {workspaceData?.name}
                </Typography>
              </Stack>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDisconnect}
                startIcon={<Icons.LinkBreak />}
                testId="disconnect-slack-button"
              >
                Disconnect
              </Button>
            </Stack>

            {/* Channel selection */}
            <SlackChannelSelect />
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
